import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon, UndoIcon } from '@hugeicons-pro/core-bulk-rounded'
import {
    ArrowDataTransferHorizontalIcon,
    ArrowDataTransferVerticalIcon,
    ArrowDown01Icon,
    DragDropHorizontalIcon,
    FlipVerticalIcon,
    UserCheck01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import {
    AccordionSummary,
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    Link,
    Typography,
    useTheme,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetMaterialsQuery } from '@/app/services/materials'
import {
    useDeleteOrderItemMutation,
    useGetOrderItemsQuery,
    useUpdateOrderItemsAttributesMutation,
} from '@/app/services/orderItems'
import { useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import { selectLocale } from '@/app/slices/appSlice'
import {
    addSelectedItem,
    removeCollapsedItem,
    removeSelectedItem,
    selectCollapsedItems,
    selectedOrderStatus,
    selectIsCalculatingOrder,
    selectSelectedItems,
    setOpenPartOrderHistoryModal,
    setSelectedOrderStatus,
    setSelectedPartFromLibrary,
} from '@/app/slices/orderItemsSlice'
import {
    selectCurrencyCode,
    selectOrganisation,
    selectOrganisationId,
    selectUseImperialUnits,
    selectWebStoreGrainDirectionEnabled,
} from '@/app/slices/organisationSlice'
import { ThumbnailImage } from '@/common/components'
import CaptionedValue from '@/common/components/CaptionedValue/CaptionedValue'
import TbxDialog from '@/common/components/TbxDialog/TbxDialog'
import { useCurrencyFormatter } from '@/common/hooks/useCurrencyFormatter'
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import FixedPriceIcon from '@/common/icons/FixedPriceIcon/FixedPriceIcon'
import LibraryIcon from '@/common/icons/LibraryIcon/LibraryIcon'
import { getThicknessDisplay, hasWebThickness, ORDER_STATUS_ARRAY } from '@/common/utils'
import DocumentsButton from '@/features/order/components/OrderAttachments/DocumentsButton'
import DocumentsDialog from '@/features/order/components/OrderAttachments/DocumentsDialog'
import OrderItemAttachments from '@/features/order/components/OrderAttachments/OrderItemAttachments'

import OrderItemStatus from '../../common/OrderItemStatus'

import ItemName from './ItemName'

const classes = {
    summaryRoot: {
        height: 74,
        backgroundColor: 'accordionSummary.main',
        '& .MuiAccordionSummary-content': {
            justifyContent: 'space-between',
            margin: '12px 0',
            '&.Mui-expanded': {
                margin: '12px 0',
            },
        },
    },
    AccordionSummaryLeft: {
        gap: 1.5,
    },

    AccordionSummaryRight: {
        gap: 6,
    },

    summaryThumbnailWrapper: {
        height: '50px',
        width: '50px',
    },

    summaryThumbnailImg: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
    },

    itemSummaryCollapsed: {
        gap: 5,
    },
    itemSummaryCollapsed_LinePrice: {
        width: '70px',
    },
    smallText: {
        fontSize: '0.75rem',
    },

    iconColor: { color: 'text.secondary' },
}

const ItemSummary = ({ hasErrors, hasIssues, hasWarnings, isUnset, listeners, orderItemId, setActivatorNodeRef }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()
    const theme = useTheme()

    const showGrainDirection = useSelector(selectWebStoreGrainDirectionEnabled)

    const organisationId = useSelector(selectOrganisationId)
    const organisationSettings = useSelector(selectOrganisation)
    const currencyCode = useSelector(selectCurrencyCode)
    const locale = useSelector(selectLocale)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const hidePrices = useSelector(selectedOrderStatus) === ORDER_STATUS_ARRAY.NotCalculated

    const { formatCurrency } = useCurrencyFormatter({
        currencyCode,
        locale,
    })

    const selectedItemsArray = useSelector(selectSelectedItems)

    const collapsedItemsArray = useSelector(selectCollapsedItems)
    const isCalculating = useSelector(selectIsCalculatingOrder)

    const { data: materials, isLoading: isLoadingMaterials } = useGetMaterialsQuery({ organisationId })

    const { data: selectedOrder, isFetching: isFetchingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [deleteOrderItem, { isLoading: isDeleting }] = useDeleteOrderItemMutation()
    const [updateOrder] = useUpdateOrderMutation()
    const [updateOrderItems] = useUpdateOrderItemsAttributesMutation()

    const [isSelected, setIsSelected] = useState(selectedItemsArray?.includes(orderItem?.id))

    const [isCollapsed, setIsCollapsed] = useState(collapsedItemsArray?.includes(orderItem?.id))

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isDocumentsOpen, setIsDocumentsOpen] = useState(false)
    const { showQuoteAttachments } = useFeatureFlags()

    const unit = useImperialUnits ? 'in' : 'mm'

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)
    const hasFixedPrice = Boolean(orderItem?.partLibraryEntry?.fixedPrice)
    const isFixedPriceExpired = useMemo(() => {
        if (!orderItem?.partLibraryEntry?.fixedPriceEndTime) return false
        const currentDate = new Date().setHours(0, 0, 0, 0)
        const fixedPriceEndTime = new Date(orderItem?.partLibraryEntry?.fixedPriceEndTime).setHours(0, 0, 0, 0)

        return currentDate > fixedPriceEndTime
    }, [orderItem?.partLibraryEntry?.fixedPriceEndTime])

    const orderMaterial = useMemo(() => {
        if (isLoadingMaterials) return null
        return materials[orderItem?.materialId]
    }, [materials, orderItem?.materialId])

    const handleUpdateOrder = async (updatedFields) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...selectedOrder,
                    ...updatedFields,
                },
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('$t(An error occurred) updating the order.'))
        }
    }

    const handleResetButtonClick = async (event) => {
        event.stopPropagation()

        try {
            const orderItems = {
                orderItemIds: [orderItem?.id],
                attributes: {
                    materialId: null,
                    thickness: null,
                    cuttingTechnologyId: null,
                    sheetId: null,
                },
            }
            await updateOrderItems({ organisationId, orderId, orderItems }).unwrap()
            enqueueSnackbar(`${orderItem?.name} ${t('reset')}`, { variant: 'success' })
        } catch (error) {
            showMutationError(error, `${t('Failed to reset')} ${orderItem?.name} from order`)
        }
    }

    const handleDeleteDialogOpen = (event) => {
        event.stopPropagation()
        setIsDeleteDialogOpen(true)
    }

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false)
    }

    const handleRemoveOrderItem = async () => {
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
            handleUpdateOrder({ status: ORDER_STATUS_ARRAY.NotCalculated })
        }
        try {
            await deleteOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
            }).unwrap()
            dispatch(removeCollapsedItem(orderItem?.id))
            dispatch(removeSelectedItem(orderItem?.id))
            enqueueSnackbar(`${orderItem?.name} ${t('deleted')}`, {
                variant: 'success',
            })
        } catch (error) {
            showMutationError(error, `${t('Failed to delete')} ${orderItem?.name} from order`)
        } finally {
            setIsDeleteDialogOpen(false)
        }
    }

    const handleSelectionChange = () => {
        setIsSelected(!isSelected)
        if (!isSelected) {
            dispatch(addSelectedItem(orderItem?.id))
        } else {
            dispatch(removeSelectedItem(orderItem?.id))
        }
    }

    const handleOpenPartOrderHistoryDialog = (e) => {
        e.stopPropagation()
        dispatch(
            setSelectedPartFromLibrary({
                partId: orderItem.partId,
                partLibraryId: orderItem.partLibraryEntryId,
                name: orderItem.name,
            })
        )
        dispatch(setOpenPartOrderHistoryModal(true))
    }

    useEffect(() => {
        if (selectedItemsArray.includes(orderItem?.id)) {
            setIsSelected(true)
        } else {
            setIsSelected(false)
        }
    }, [selectedItemsArray, orderItem?.id])

    useEffect(() => {
        setIsCollapsed(collapsedItemsArray.includes(orderItem?.id))
    }, [collapsedItemsArray, orderItem?.id])

    return (
        <>
            <AccordionSummary
                aria-controls={`panel${orderItem?.id}-content`}
                aria-label="Expand"
                expandIcon={
                    <HugeiconsIcon
                        color={theme.palette.secondary.main}
                        icon={ArrowDown01Icon}
                    />
                }
                id={`panel${orderItem?.id}-header`}
                sx={classes.summaryRoot}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    sx={classes.AccordionSummaryLeft}
                >
                    <Typography variant="body1">{orderItem?.index + 1}</Typography>

                    <IconButton
                        aria-label="Drag"
                        color="secondary"
                        size="small"
                        {...listeners}
                        ref={setActivatorNodeRef}
                        sx={{ cursor: 'grab' }}
                    >
                        <HugeiconsIcon icon={DragDropHorizontalIcon} />
                    </IconButton>

                    <Checkbox
                        checked={isSelected}
                        color="primary"
                        inputProps={{
                            'data-testid': 'select-order-item',
                        }}
                        size="small"
                        sx={{ p: 0 }}
                        onChange={handleSelectionChange}
                        onClick={(event) => event.stopPropagation()}
                    />

                    <Box
                        alignItems="center"
                        display="flex"
                        gap={2}
                    >
                        {isUnset ? (
                            <OrderItemStatus
                                messages="Missing settings to calculate part"
                                status="unset"
                            />
                        ) : null}

                        {!hasIssues && !isUnset ? <OrderItemStatus /> : null}

                        {hasIssues && hasWarnings && !isUnset ? (
                            <OrderItemStatus
                                messages={hasWarnings}
                                status="warning"
                            />
                        ) : null}
                        {hasIssues && hasErrors && !isUnset ? (
                            <OrderItemStatus
                                messages={hasErrors}
                                status="error"
                            />
                        ) : null}

                        {isCollapsed ? (
                            <ThumbnailImage
                                uri={orderItem?.thumbnailUri}
                                wrapperHeight={50}
                                wrapperWidth={50}
                            />
                        ) : null}

                        <ItemName
                            id={orderItem?.id}
                            isPartFromLibrary={isPartFromLibrary}
                            name={orderItem?.name}
                        />
                    </Box>
                </Box>

                <Box
                    alignItems="center"
                    display="flex"
                    sx={classes.AccordionSummaryRight}
                >
                    {isCalculating || isFetchingOrder ? (
                        <Box
                            alignItems="center"
                            data-testid="calculating-header"
                            display="flex"
                            justifyContent="center"
                        >
                            <Typography
                                color="secondary"
                                letterSpacing={1}
                                mr={2}
                                textTransform={'uppercase'}
                                variant="strong2"
                            >
                                {isCalculating ? t('Calculating') : t('Loading')}
                            </Typography>
                            <CircularProgress
                                color="secondary"
                                size={24}
                            />
                        </Box>
                    ) : null}

                    {!isCollapsed && isPartFromLibrary ? (
                        <>
                            {hasFixedPrice ? (
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    style={{ gap: '4px' }}
                                >
                                    <FixedPriceIcon
                                        fontSize="small"
                                        sx={isFixedPriceExpired ? { color: 'error.main' } : classes.iconColor}
                                    />
                                    <Typography
                                        color={isFixedPriceExpired ? 'error.main' : 'text.secondary'}
                                        variant="body2"
                                    >
                                        {isFixedPriceExpired ? t('Fixed price expired') : t('Fixed price')}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Box
                                alignItems="center"
                                display="flex"
                                style={{ gap: '4px' }}
                            >
                                <LibraryIcon
                                    fontSize="small"
                                    sx={classes.iconColor}
                                />
                                <Link
                                    color="info"
                                    variant="body2"
                                    onClick={(event) => handleOpenPartOrderHistoryDialog(event)}
                                >
                                    {t('Part from library')}
                                </Link>
                            </Box>
                        </>
                    ) : null}

                    {!hasErrors && isCollapsed && !isCalculating && !isFetchingOrder ? (
                        <Box
                            display="flex"
                            sx={classes.itemSummaryCollapsed}
                        >
                            {isPartFromLibrary ? (
                                <Box
                                    alignItems="flex-end"
                                    className="itemSummaryCollapsed_Thumbnail"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {t('Part ID:')}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        gap={1}
                                    >
                                        {hasFixedPrice ? (
                                            <FixedPriceIcon
                                                data-testid="fixed-price-icon"
                                                fontSize="small"
                                                sx={[isFixedPriceExpired ? { color: 'error.main' } : classes.iconColor]}
                                            />
                                        ) : null}
                                        <LibraryIcon
                                            data-testid="part-library-icon"
                                            fontSize="small"
                                            sx={classes.iconColor}
                                        />
                                        <Link
                                            color="text.secondary"
                                            variant="body2"
                                            onClick={(event) => handleOpenPartOrderHistoryDialog(event)}
                                        >
                                            {orderItem?.partId}
                                        </Link>
                                    </Box>
                                </Box>
                            ) : null}

                            {showGrainDirection && orderItem?.grainDirection !== 'None' ? (
                                <Box
                                    alignItems="flex-end"
                                    className="itemSummaryCollapsed_GrainDirection"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {t('Grain direction')}
                                    </Typography>

                                    <HugeiconsIcon
                                        altIcon={ArrowDataTransferHorizontalIcon}
                                        color="textSecondary"
                                        icon={ArrowDataTransferVerticalIcon}
                                        showAlt={orderItem?.grainDirection === 'Horizontal'}
                                        size={20}
                                    />
                                </Box>
                            ) : null}

                            {orderItem?.materialId ? (
                                <Box
                                    alignItems="flex-end"
                                    className="itemSummaryCollapsed_LinePrice"
                                    display="flex"
                                    flexDirection="column"
                                >
                                    <Typography
                                        sx={classes.smallText}
                                        variant="body2"
                                    >
                                        {orderMaterial?.materialName}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        gap={1}
                                    >
                                        {orderItem?.isCustomerSuppliedMaterial ? (
                                            <HugeiconsIcon
                                                icon={UserCheck01Icon}
                                                size={18}
                                            />
                                        ) : null}
                                        <Box
                                            alignItems={'center'}
                                            display="flex"
                                            gap={0.5}
                                        >
                                            <HugeiconsIcon
                                                color="textSecondary"
                                                icon={FlipVerticalIcon}
                                                size={18}
                                            />
                                            <CaptionedValue
                                                caption={hasWebThickness(orderItem) ? t('web / flange') : ''}
                                            >
                                                <Typography variant="body2">
                                                    {getThicknessDisplay(orderItem, String)} {unit}
                                                </Typography>
                                            </CaptionedValue>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null}
                            <Box
                                alignItems="flex-end"
                                className="itemSummaryCollapsed_Quantity"
                                display="flex"
                                flexDirection="column"
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Quantity')}
                                </Typography>
                                <Typography variant="body2">x {orderItem?.quantity}</Typography>
                            </Box>
                            <Box
                                alignItems="flex-end"
                                className="itemSummaryCollapsed_UnitPrice"
                                display="flex"
                                flexDirection="column"
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Unit price')}
                                </Typography>

                                {isCalculating ? (
                                    <CircularProgress
                                        size={14}
                                        sx={classes.calculatingIcon}
                                    />
                                ) : null}

                                {!hidePrices && !isCalculating ? (
                                    <Typography variant="body2">
                                        {orderItem?.itemPrice
                                            ? formatCurrency(orderItem?.itemPrice)
                                            : String.fromCharCode(8212)}
                                    </Typography>
                                ) : (
                                    <Typography variant="strong2">{String.fromCharCode(8212)}</Typography>
                                )}
                            </Box>
                            <Box
                                alignItems="flex-end"
                                display="flex"
                                flexDirection="column"
                                sx={classes.itemSummaryCollapsed_LinePrice}
                            >
                                <Typography
                                    sx={classes.smallText}
                                    variant="body2"
                                >
                                    {t('Line price')}
                                </Typography>

                                {isCalculating ? (
                                    <CircularProgress
                                        size={14}
                                        sx={classes.calculatingIcon}
                                    />
                                ) : null}

                                {!hidePrices && !isCalculating ? (
                                    <Typography variant="strong2">
                                        {orderItem?.linePrice
                                            ? formatCurrency(orderItem?.linePrice)
                                            : String.fromCharCode(8212)}
                                    </Typography>
                                ) : (
                                    <Typography variant="strong2">{String.fromCharCode(8212)}</Typography>
                                )}
                            </Box>
                        </Box>
                    ) : null}

                    <Box
                        alignItems="center"
                        display="flex"
                    >
                        {hasErrors ? (
                            <IconButton
                                aria-label="reset"
                                color="secondary"
                                onClick={(event) => handleResetButtonClick(event)}
                                onFocus={(event) => event.stopPropagation()}
                            >
                                <HugeiconsIcon
                                    icon={UndoIcon}
                                    size={20}
                                />
                            </IconButton>
                        ) : null}

                        {showQuoteAttachments ? (
                            <DocumentsButton
                                attachments={orderItem?.attachments}
                                onClick={() => setIsDocumentsOpen(true)}
                            />
                        ) : null}

                        <IconButton
                            aria-label="delete"
                            color="secondary"
                            data-testid="delete-order-item-name"
                            size="small"
                            onClick={(event) => handleDeleteDialogOpen(event)}
                            onFocus={(event) => event.stopPropagation()}
                        >
                            <HugeiconsIcon
                                icon={Delete02Icon}
                                size={20}
                            />
                        </IconButton>
                    </Box>
                </Box>
            </AccordionSummary>
            <TbxDialog
                closeButtonText={t('cancel')}
                confirmButtonText={isDeleting ? t('Deleting...') : t('Yes, delete')}
                content={`${t('Are you sure you want to delete part')} ${orderItem?.name}`}
                handleClose={handleDeleteDialogClose}
                handleConfirmClose={handleRemoveOrderItem}
                isLoading={isDeleting}
                isOpen={isDeleteDialogOpen}
                title={`${t('Delete part')} ${orderItem?.name}?`}
                uniqueKey={orderItemId}
            />
            {showQuoteAttachments ? (
                <DocumentsDialog
                    itemName={orderItem?.name}
                    open={isDocumentsOpen}
                    subtitle={t('Attachments for this part only')}
                    onClose={() => setIsDocumentsOpen(false)}
                >
                    <OrderItemAttachments orderItemId={orderItemId} />
                </DocumentsDialog>
            ) : null}
        </>
    )
}

ItemSummary.propTypes = {
    hasErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    hasIssues: PropTypes.bool,
    hasWarnings: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    isUnset: PropTypes.bool,
    listeners: PropTypes.object,
    orderItemId: PropTypes.string,
    setActivatorNodeRef: PropTypes.func,
}

export default memo(ItemSummary)
